import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import Hero from "../components/Hero";
import ComboDealCard from "../components/ComboDealCard";
import MenuSection from "../components/MenuSection";
import ComboBuilderDialog from "../components/ComboBuilderDialog";
import CartDrawer from "../components/CartDrawer";
import StoreStatusBanner from "../components/StoreStatusBanner";
import ClosedBanner from "../components/ClosedBanner";
import AuthModal from "../components/AuthModal";
import { api } from "../lib/api";
import { Toaster } from "sonner";
import { Phone, AlertTriangle } from "lucide-react";

const PHONE = "07876664591";

export default function Home() {
  const [menu, setMenu] = useState(null);
  const [comboOpen, setComboOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);

  useEffect(() => {
    api.get("/menu").then(({ data }) => setMenu(data)).catch(console.error);
  }, []);

  if (!menu) {
    return (
      <div className="min-h-screen bg-ink text-cream flex items-center justify-center font-heading uppercase tracking-widest">
        Loading menu…
      </div>
    );
  }

  const allergenLabels = menu.allergen_labels || {};
  const minOrder = menu.min_order_gbp || 10;
  const deliveryFee = menu.delivery_fee_gbp || 2;

  return (
    <div className="min-h-screen bg-ink text-cream">
      <Toaster position="top-right" theme="dark" toastOptions={{
        style: { background: "#0a0a0a", border: "1px solid #F59E0B", color: "#FDFBF7", borderRadius: 0 },
      }} />
      <ClosedBanner store={menu.store} />
      <StoreStatusBanner store={menu.store} />
      <Header onSignInClick={() => setAuthOpen(true)} />

      {/* Menu nav scroll bar */}
      <nav className="sticky top-20 z-30 bg-ink border-b border-border overflow-x-auto scrollbar-hide">
        <div className="flex min-w-max px-4 py-0">
          {[
            { label: "Combo", href: "#combo" },
            { label: "Pasta", href: "#pasta" },
            { label: "Wings", href: "#wings" },
            { label: "Sides", href: "#sides" },
            { label: "Desserts", href: "#desserts" },
            { label: "Drinks", href: "#drinks" },
            { label: "Extras", href: "#extras" },
          ].map(({ label, href }) => (
            <a
              key={label}
              href={href}
              onClick={e => {
                e.preventDefault();
                const el = document.querySelector(href);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="shrink-0 px-5 py-3 font-heading uppercase tracking-widest text-sm text-cream/60 hover:text-gold border-b-2 border-transparent hover:border-gold transition-colors whitespace-nowrap"
            >
              {label}
            </a>
          ))}
        </div>
      </nav>

      <Hero onOpenCombo={() => setComboOpen(true)} />
      <ComboDealCard onClick={() => setComboOpen(true)} />

      <div id="menu">
        <MenuSection id="pasta" title="Pasta Boxes — £9" items={menu.menu.pasta} columns={2} allergenLabels={allergenLabels} />
        <MenuSection id="wings" title="Wings — 5 / £5 · 10 / £9" items={menu.menu.wings} columns={2} allergenLabels={allergenLabels} />
        <MenuSection id="sides" title="Sides" items={menu.menu.sides} columns={3} allergenLabels={allergenLabels} />
        <MenuSection id="desserts" title="Desserts" items={menu.menu.desserts} columns={3} allergenLabels={allergenLabels} />
        <MenuSection id="drinks" title="Drinks — £1.50" items={menu.menu.drinks} columns={3} allergenLabels={allergenLabels} />
        <MenuSection id="extras" title="Extras" items={menu.menu.extras} columns={3} allergenLabels={allergenLabels} />
      </div>

      {/* Allergy Banner */}
      <section className="bg-accent/10 border-y-2 border-accent/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-accent shrink-0 mt-0.5" />
            <div>
              <div className="font-heading uppercase tracking-widest text-accent text-base">Food Allergy or Intolerance?</div>
              <p className="text-cream/80 text-sm mt-1">
                Do <strong>not</strong> order online. Call us before placing your order so we can advise you safely.
              </p>
            </div>
          </div>
          <a href={`tel:${PHONE}`} className="shrink-0 inline-flex items-center gap-2 px-5 py-3 border-2 border-accent text-accent font-heading uppercase tracking-wider text-lg hover:bg-accent hover:text-black transition-colors">
            <Phone className="h-5 w-5" /> {PHONE}
          </a>
        </div>
      </section>

      <section id="delivery" className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-heading uppercase tracking-tight text-cream text-4xl sm:text-5xl">Delivery & Hours</h2>
            <p className="mt-4 text-cream/75 max-w-md">
              We deliver hot and fast across <span className="text-gold font-semibold">Partington, Carrington & Warburton</span>.
            </p>
            <ul className="mt-4 text-cream/80 text-sm space-y-1">
              <li>· £{minOrder.toFixed(2)} minimum order</li>
              <li>· £{deliveryFee.toFixed(2)} delivery fee</li>
              <li>· Hours: {menu.store?.hours_label} on {menu.store?.open_days?.join(", ")}</li>
              <li>· Pay by card or cash on delivery</li>
              <li className="text-gold">· Loyalty: 10 orders → free 5 wings · 25 → free combo</li>
            </ul>
            <a href={`tel:${PHONE}`} className="mt-6 inline-flex items-center gap-2 text-gold font-heading uppercase tracking-wider text-xl hover:text-amber-400 transition-colors">
              <Phone className="h-5 w-5" /> {PHONE}
            </a>
          </div>
          <div className="poster-card p-8 text-center">
            <div className="font-heading text-gold text-5xl uppercase">Local Only</div>
            <div className="font-heading text-cream tracking-widest mt-3 text-lg">Partington · Carrington · Warburton</div>
            <div className="mt-4 inline-block px-3 py-1 border border-gold/40 text-gold/90 font-heading uppercase tracking-widest text-xs">
              {menu.store?.is_open ? "Open Now" : `Re-opens ${menu.store?.next_open_label || "soon"}`}
            </div>
            <div className="mt-6 border-t border-border pt-5">
              <div className="font-heading uppercase tracking-widest text-cream/60 text-xs mb-2">Call to Order or Enquire</div>
              <a href={`tel:${PHONE}`} className="font-heading text-gold text-2xl tracking-wider hover:text-amber-400 transition-colors flex items-center justify-center gap-2">
                <Phone className="h-5 w-5" /> {PHONE}
              </a>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-black border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="font-heading uppercase tracking-widest text-cream/60 text-sm">
              © {new Date().getFullYear()} Loaded Street Kitchen — Loaded Food. Big Flavour.
            </div>
            <a href={`tel:${PHONE}`} className="inline-flex items-center gap-2 text-gold font-heading tracking-wider hover:text-amber-400 transition-colors">
              <Phone className="h-4 w-4" /> {PHONE}
            </a>
            <a href="/admin/login" className="text-cream/40 hover:text-gold text-xs uppercase tracking-widest" data-testid="admin-link">Staff</a>
          </div>
        </div>
      </footer>

      <CartDrawer
        minOrder={minOrder}
        deliveryFee={deliveryFee}
        pastaFlavours={menu.pasta_flavours}
        wingFlavours={menu.wing_flavours}
        onSignInClick={() => setAuthOpen(true)}
      />
      <ComboBuilderDialog
        open={comboOpen}
        onOpenChange={setComboOpen}
        pastaFlavours={menu.pasta_flavours}
        wingFlavours={menu.wing_flavours}
        comboPrice={menu.combo_price}
      />
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  );
}
